import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaReact, FaHtml5, FaCss3, FaJs } from 'react-icons/fa';
import projectsData from '../data/projects.json';

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    // Load projects from the JSON file
    setProjects(projectsData);
  }, []);

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'featured', label: 'Featured' },
    { id: 'web', label: 'Web Development' }
  ];

  const filteredProjects = projects.filter(project => 
    activeCategory === 'all' || project.category === activeCategory
  );

  // Function to get icon based on technology name
  const getTechIcon = (tech) => {
    switch(tech) {
      case 'React': return <FaReact />;
      case 'HTML5': return <FaHtml5 />;
      case 'CSS3': return <FaCss3 />;
      case 'JavaScript': return <FaJs />;
      default: return null;
    }
  };

  return (
    <ProjectsContainer>
      <ProjectsHeader>
        <Title
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          My Projects
        </Title>
        <CategoryFilter>
          {categories.map(category => (
            <CategoryButton
              key={category.id}
              $active={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.label}
            </CategoryButton>
          ))}
        </CategoryFilter>
      </ProjectsHeader>

      <ProjectGrid>
        <AnimatePresence mode="sync">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              <ProjectImageContainer>
                <ProjectImage src={project.image} alt={project.title} />
                <ProjectOverlay>
                  <TechStack>
                    {project.technologies.map((tech, index) => (
                      <TechBadge key={index}>
                        {getTechIcon(tech)}
                        <span>{tech}</span>
                      </TechBadge>
                    ))}
                  </TechStack>
                </ProjectOverlay>
              </ProjectImageContainer>
              
              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <ProjectLinks>
                  <GithubLink 
                    href={project.github} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaGithub /> View Code
                  </GithubLink>
                  {project.demo && (
                    <DemoLink 
                      href={project.demo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaExternalLinkAlt /> Live Demo
                    </DemoLink>
                  )}
                </ProjectLinks>
              </ProjectContent>
            </ProjectCard>
          ))}
        </AnimatePresence>
      </ProjectGrid>
    </ProjectsContainer>
  );
};

const ProjectsContainer = styled.div`
  padding: 100px 20px 50px;
  max-width: 1200px;
  margin: 0 auto;

  h1 {
    text-align: center;
    margin-bottom: 50px;
    color: var(--text-primary);
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  padding: 0 20px;
`;

const ProjectCard = styled(motion.div)`
  background: var(--secondary);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ProjectImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 10px 10px 0 0;
`;

const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${ProjectImageContainer}:hover & {
    opacity: 1;
  }
`;

const TechStack = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  padding: 1rem;
`;

const TechBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  color: var(--accent);
  font-size: 0.9rem;

  svg {
    font-size: 1.1rem;
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 10px 10px 0 0;
  margin-bottom: 1rem;
`;

const ProjectContent = styled.div`
  padding: 0 1rem;
`;

const ProjectTitle = styled.h3`
  color: var(--accent);
  margin-bottom: 1rem;
`;

const ProjectDescription = styled.p`
  color: var(--text-primary);
  margin-bottom: 1.5rem;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const GithubLink = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--accent);
  border-radius: var(--border-radius);
  transition: var(--transition);

  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
    color: var(--accent);
  }
`;

const DemoLink = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--accent);
  border-radius: var(--border-radius);
  transition: var(--transition);

  &:hover {
    background-color: rgba(100, 255, 218, 0.1);
    color: var(--accent);
  }
`;

const ProjectsHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  color: var(--text-primary);
  margin-bottom: 30px;
`;

const CategoryFilter = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const CategoryButton = styled(motion.button)`
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.$active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.$active ? 'rgba(100, 255, 218, 0.1)' : 'transparent'};
  color: ${props => props.$active ? 'var(--accent)' : 'var(--text-primary)'};
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 14px;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

export default Projects;